import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEndpoints } from '../../../hooks/useEndpoints';
import {
  effortAthleteEndpoints,
  IEffortAthleteResponse,
  IEffortSeriesPoint,
} from '../../endpoints/effort-athlete';
import type { HistoryEntry, ReadinessHistoryResponse } from '../../hooks/useAthleteReadiness';
import { MetricKey, MetricPeriod } from './metricHistory.constants';

/** Punto normalizzato del chart: stessa forma per tutte le metriche. */
export interface MetricHistoryPoint {
  /** YYYY-MM-DD */
  date: string;
  value: number;
}

export interface MetricHistoryStats {
  avg: number;
  min: number;
  max: number;
}

/** Metriche alimentate da `effort/athlete` (serie sRPE/ACWR) invece che da `readiness/history`. */
const EFFORT_METRICS: MetricKey[] = ['srpe', 'acwr'];

function isEffortMetric(metric: MetricKey): boolean {
  return EFFORT_METRICS.includes(metric);
}

function extractReadinessValue(entry: HistoryEntry, metric: MetricKey): number | null {
  switch (metric) {
    case 'ri':
      return entry.ri ?? null;
    case 'cmj':
      return entry.cmj ?? null;
    case 'hrv':
      return entry.hrv ?? null;
    case 'fcr':
      return entry.fcr ?? null;
    default:
      return null;
  }
}

function extractEffortValue(point: IEffortSeriesPoint, metric: MetricKey): number | null {
  if (metric === 'srpe') return point.srpe;
  if (metric === 'acwr') return point.acwr;
  return null;
}

/**
 * Storico normalizzato di una metrica per il drill-down `MetricHistoryModal`.
 * Riusa gli STESSI endpoint di `useAthleteEffort` (sRPE/ACWR) e
 * `useAthleteReadiness` (RI/CMJ/HRV/HRrest) — nessuna nuova rotta backend.
 */
export function useMetricHistory(
  athleteId: string | null,
  metric: MetricKey | null,
  period: MetricPeriod,
) {
  const endpoints = useEndpoints();
  const effortMode = !!metric && isEffortMetric(metric);

  const readinessQuery = useQuery<ReadinessHistoryResponse>({
    queryKey: ['metric-history-readiness', athleteId, period],
    queryFn: async () => {
      const { data } = await endpoints.getReadinessHistory(athleteId as string, {
        limit: period,
      });
      return data;
    },
    enabled: !!athleteId && !!metric && !effortMode,
  });

  const effortQuery = useQuery<IEffortAthleteResponse>({
    queryKey: ['metric-history-effort', athleteId, period],
    queryFn: async () => {
      const { data } = await effortAthleteEndpoints.getAthleteEffort(athleteId as string, {
        days: period,
      });
      return data;
    },
    enabled: !!athleteId && !!metric && effortMode,
  });

  const activeQuery = effortMode ? effortQuery : readinessQuery;

  const points: MetricHistoryPoint[] = useMemo(() => {
    if (!metric) return [];

    if (effortMode) {
      const series = effortQuery.data?.series ?? [];
      return series
        .map((p) => {
          const value = extractEffortValue(p, metric);
          return value != null ? { date: p.date, value } : null;
        })
        .filter((d): d is MetricHistoryPoint => d !== null);
    }

    // readiness/history torna ordinato per data discendente — riordina ascendente per il chart
    const history = [...(readinessQuery.data?.history ?? [])].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
    return history
      .map((entry) => {
        const value = extractReadinessValue(entry, metric);
        return value != null ? { date: entry.date, value } : null;
      })
      .filter((d): d is MetricHistoryPoint => d !== null);
  }, [metric, effortMode, effortQuery.data, readinessQuery.data]);

  const stats: MetricHistoryStats | null = useMemo(() => {
    if (points.length === 0) return null;
    const values = points.map((p) => p.value);
    const avg = values.reduce((s, v) => s + v, 0) / values.length;
    return { avg, min: Math.min(...values), max: Math.max(...values) };
  }, [points]);

  return {
    points,
    stats,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching,
    isError: activeQuery.isError,
    refetch: activeQuery.refetch,
  };
}
